'use client'

import { IconChevronDown } from '@macarongfactory/web-icons'
import {
  BottomSheet,
  Chip,
  RightSelectRow,
  Text,
} from '@macarongfactory/web-ui'
import { useEffect, useMemo, useRef, useState } from 'react'

/** 유종/세그먼트 선택 — 데스크탑 팝오버 / 모바일 BottomSheet (FuelTypeFilter UX 복제) */
const PC_MEDIA_QUERY = '(min-width: 1024px)'
const CONFIRM_TEXT = '확인'
const CHIP_CHEVRON_SIZE_PX = 16
const UNDERLINE_CHEVRON_SIZE_PX = 24

interface IRankSelectSheetProps {
  /** 트리거 형태 — 유종은 칩, 모바일 세그먼트는 밑줄 텍스트 */
  triggerVariant: 'chip' | 'underline'
  /** 현재 값 */
  value: string
  /** 선택 가능한 옵션 */
  options: string[]
  /** 값 변경 (URL 갱신 → 서버 재조회) */
  onChange: (value: string) => void
  /** 트리거(칩/밑줄) 클릭 시 — 트래킹용 (열림 가능할 때만 호출) */
  onTriggerClick?: () => void
  /** 다이얼로그 타이틀 */
  title: string
}

const OptionList = ({
  options,
  selected,
  onSelect,
}: {
  options: string[]
  selected: string
  onSelect: (value: string) => void
}) => (
  <div className='flex flex-col'>
    {options.map(option => {
      const isSelected = option === selected
      return (
        <RightSelectRow
          key={option}
          verticalPadding='dimension-x3'
          title={
            <RightSelectRow.Main.Title.Text
              text={option}
              typography='title-1-medium'
              color={
                isSelected
                  ? 'text-foreground-brand-strong'
                  : 'text-foreground-neutral-normal'
              }
            />
          }
          right={<RightSelectRow.Right.Checkmark />}
          selected={isSelected}
          clickable={() => onSelect(option)}
        />
      )
    })}
  </div>
)

const RankSelectSheet = ({
  triggerVariant,
  value,
  options,
  onChange,
  onTriggerClick,
  title,
}: IRankSelectSheetProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempValue, setTempValue] = useState(value)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const displayOptions = useMemo(
    () => (options.length === 0 ? [value] : options),
    [options, value]
  )
  const canSwitch = displayOptions.length > 1

  const handleOpen = () => {
    if (!canSwitch) return
    onTriggerClick?.()
    setTempValue(value)
    setIsOpen(true)
  }
  const handleClose = () => setIsOpen(false)
  const handleMobileConfirm = () => {
    setIsOpen(false)
    onChange(tempValue)
  }
  const handlePcSelect = (next: string) => {
    setIsOpen(false)
    onChange(next)
  }

  // PC 팝오버 바깥 클릭 시 닫기 (모바일 BottomSheet는 자체 backdrop)
  useEffect(
    function closePcPopoverOnOutsideClick() {
      if (!isOpen) return
      if (typeof window === 'undefined') return
      if (!window.matchMedia(PC_MEDIA_QUERY).matches) return

      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target as Node | null
        if (!target) return
        if (containerRef.current?.contains(target)) return
        setIsOpen(false)
      }
      window.addEventListener('pointerdown', handlePointerDown)
      return () => window.removeEventListener('pointerdown', handlePointerDown)
    },
    [isOpen]
  )

  const trigger =
    triggerVariant === 'chip' ? (
      <Chip.Filter
        text={value}
        variant='outline'
        selected={false}
        trailingIcon={
          <IconChevronDown
            width={CHIP_CHEVRON_SIZE_PX}
            height={CHIP_CHEVRON_SIZE_PX}
          />
        }
        clickable={handleOpen}
      />
    ) : (
      <button
        type='button'
        onClick={handleOpen}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        disabled={!canSwitch}
        className='flex items-center gap-dimension-x1 rounded-dimension-x3 py-dimension-x1 pr-dimension-x1'
      >
        <Text
          variant='display-1-bold'
          color='text-foreground-brand-strong'
          className='underline decoration-[2.4px] underline-offset-[7.5px]'
        >
          {value}
        </Text>
        <span className='inline-flex text-foreground-brand-strong'>
          <IconChevronDown
            width={UNDERLINE_CHEVRON_SIZE_PX}
            height={UNDERLINE_CHEVRON_SIZE_PX}
          />
        </span>
      </button>
    )

  return (
    <div className='shrink-0'>
      <div ref={containerRef} className='relative inline-block'>
        {trigger}

        {isOpen ? (
          <ul
            aria-label={title}
            className='shadow-weak absolute left-0 top-[calc(100%+8px)] z-10 hidden min-w-[160px] flex-col rounded-dimension-x3 bg-background-surface-default py-dimension-x2 lg:flex'
          >
            {displayOptions.map(option => {
              const isSelected = option === value
              return (
                <li key={option}>
                  <button
                    type='button'
                    aria-current={isSelected ? 'true' : undefined}
                    onClick={() => handlePcSelect(option)}
                    className='flex w-full items-center px-dimension-x4 py-dimension-x3 text-left hover:bg-background-neutral-weak'
                  >
                    <Text
                      variant={
                        isSelected ? 'title-2-semibold' : 'title-2-medium'
                      }
                      color={
                        isSelected
                          ? 'text-foreground-brand-strong'
                          : 'text-foreground-neutral-normal'
                      }
                    >
                      {option}
                    </Text>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>

      <div className='lg:hidden'>
        <BottomSheet
          open={isOpen}
          onClose={handleClose}
          className='left-0 right-0 mx-auto max-w-[480px]'
          top={
            <BottomSheet.Top.LeftAlignedHeader
              title={title}
              titleTypography='heading-3-bold'
            />
          }
          content={
            <BottomSheet.Content>
              <OptionList
                options={displayOptions}
                selected={tempValue}
                onSelect={setTempValue}
              />
            </BottomSheet.Content>
          }
          bottom={
            <BottomSheet.Bottom.BottomCTA.Vertical
              mainButton={
                <BottomSheet.Bottom.MainButton.SolidButton
                  text={CONFIRM_TEXT}
                  onClick={handleMobileConfirm}
                />
              }
            />
          }
        />
      </div>
    </div>
  )
}

export default RankSelectSheet
